import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { move } from "../../lib/adminHelpers";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { UploadField } from "./UploadField";
import { RowControls, DraftBadge } from "./RowControls";
import { AudioPreview } from "./AudioPreview";

const newVariant = (fx = "") => ({ _uid: crypto.randomUUID(), fx, audio_path: "" });
const emptyVoice = () => ({ title: "", variants: [newVariant("Clean"), newVariant("")] });

const VoiceFields = ({ f, set, prefix }) => {
  const upd = (i, patch) => set({ ...f, variants: f.variants.map((v, x) => (x === i ? { ...v, ...patch } : v)) });
  return (
    <div className="space-y-3">
      <div><Label className="text-xs text-[#A1A1AA]">Voice / character name</Label>
        <Input value={f.title} onChange={(e) => set({ ...f, title: e.target.value })} placeholder="Infernal Queen" data-testid={`${prefix}-title`} className="bg-[#0B0C10] border-white/10 mt-1" /></div>
      <div className="flex items-center justify-between">
        <Label className="text-xs text-[#A1A1AA]">Variants ({f.variants.length}) — first is usually "Clean"</Label>
        <Button type="button" onClick={() => set({ ...f, variants: [...f.variants, newVariant("")] })} size="sm" variant="outline" data-testid={`${prefix}-add-variant`} className="h-7 border-white/15 bg-transparent text-white hover:bg-white/5 rounded-sm text-xs"><Plus size={13} className="mr-1" /> Add variant</Button>
      </div>
      {f.variants.map((v, i) => (
        <div key={v._uid || i} className="rounded border border-white/10 p-3 space-y-2 relative">
          <button onClick={() => set({ ...f, variants: f.variants.filter((_, x) => x !== i) })} data-testid={`${prefix}-rm-variant-${i}`} className="absolute top-2 right-2 text-[#E04F5F] hover:opacity-70"><X size={14} /></button>
          <Input value={v.fx} onChange={(e) => upd(i, { fx: e.target.value })} placeholder="Label (e.g. Clean, Demon, Robotic)" data-testid={`${prefix}-variant-fx-${i}`} className="bg-[#0B0C10] border-white/10 h-8 text-sm pr-8" />
          <UploadField label="" testid={`${prefix}-variant-audio-${i}`} current={v.audio_path ? "Audio set" : ""} onUploaded={(p) => upd(i, { audio_path: p })} />
        </div>
      ))}
    </div>
  );
};

export const VoiceLabManager = ({ bump, onChange }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyVoice());
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/voice-samples").then((r) => setItems(r.data)).catch(() => {});
  const done = () => { load(); onChange && onChange(); };
  useEffect(() => { load(); }, [bump]);

  const clean = (f) => ({ ...f, variants: f.variants.filter((v) => v.fx && v.audio_path).map(({ fx, audio_path }) => ({ fx, audio_path })) });
  const add = async () => {
    if (!form.title) { toast.error("Voice name required"); return; }
    const c = clean(form);
    if (c.variants.length < 1) { toast.error("Add at least one variant with a label + audio"); return; }
    await api.post("/admin/voice-samples", { ...c, status: "draft", order: items.length });
    toast.success("Added as Draft — click Go live to publish"); setForm(emptyVoice()); done();
  };
  const save = async () => {
    if (!editing.title) { toast.error("Voice name required"); return; }
    const c = clean(editing);
    if (c.variants.length < 1) { toast.error("Add at least one variant with a label + audio"); return; }
    await api.put(`/admin/voice-samples/${editing.id}`, { ...c, id: editing.id });
    toast.success("Saved"); setEditing(null); done();
  };
  const publish = async (it) => { await api.put(`/admin/voice-samples/${it.id}`, { ...it, status: "published" }); toast.success("Published"); done(); };
  const del = async (id) => { await api.delete(`/admin/voice-samples/${id}`); done(); };
  const toggleHide = async (it) => { await api.put(`/admin/voice-samples/${it.id}`, { ...it, hidden: !it.hidden }); done(); };
  const reorder = async (i, dir) => { const next = move(items, i, dir); if (!next) return; await api.post("/admin/voice-samples/reorder", { ids: next.map((x) => x.id) }); load(); };

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="rounded-lg border border-white/10 bg-[#12141A] p-6 h-fit">
        <h3 className="font-head text-lg mb-4 flex items-center gap-2"><Plus size={18} className="text-[#C8A97E]" /> Add Voice</h3>
        <VoiceFields f={form} set={setForm} prefix="fx" />
        <Button onClick={add} data-testid="fx-add" className="w-full mt-4 bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] font-semibold rounded-sm">Add Voice</Button>
      </div>

      <div className="space-y-2">
        {items.length === 0 && <p className="text-[#A1A1AA]">No voices yet.</p>}
        {items.map((v, i) => (
          <div key={v.id} data-testid={`fx-row-${v.id}`} className={`flex items-center justify-between gap-3 rounded-lg border p-3 ${v.hidden ? "border-white/5 bg-[#0e0f14] opacity-60" : "border-white/10 bg-[#12141A]"}`}>
            <div className="min-w-0">
              <div className="font-medium truncate flex items-center gap-2">{v.title}<DraftBadge show={v.status === "draft"} />{v.hidden && <span className="text-[10px] uppercase text-[#E0A44F]">hidden</span>}</div>
              <div className="text-xs text-[#A1A1AA] truncate">{(v.variants || []).map((x) => x.fx).join(" · ")}</div>
            </div>
            <RowControls testid={`fx-${v.id}`} hidden={v.hidden} isFirst={i === 0} isLast={i === items.length - 1}
              isDraft={v.status === "draft"} onPublish={() => publish(v)}
              onUp={() => reorder(i, -1)} onDown={() => reorder(i, 1)}
              onToggleHide={() => toggleHide(v)} onEdit={() => setEditing({ ...v, variants: (v.variants || []).map((x) => ({ _uid: crypto.randomUUID(), ...x })) })} onDelete={() => del(v.id)}
              extra={<AudioPreview src={v.variants?.[0]?.audio_path} testid={`fx-preview-${v.id}`} />} />
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#12141A] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="fx-edit-dialog">
          <DialogHeader><DialogTitle className="font-head">Edit Voice</DialogTitle></DialogHeader>
          {editing && <VoiceFields f={editing} set={setEditing} prefix="fx-edit" />}
          <Button onClick={save} data-testid="fx-save" className="w-full mt-2 bg-[#C8A97E] text-[#0B0C10] hover:bg-[#B3956D] font-semibold rounded-sm">Save Changes</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
